/**
 * Pickups (workstream C) — the supervisor's home for warehouse runs. A pickup is
 * one trip that covers N projects' orders; the crew check the linked projects'
 * packing lists off as "received". This page lists them and creates new ones
 * (pick the projects, add the ordered stops, set a window); crew assignment +
 * the checklist live on the detail page.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, PackageCheck, Plus, Truck } from 'lucide-react';
import { toast } from 'sonner';
import { useCreatePickup, usePickups } from '@/api/pickups';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { Dialog } from '@/components/ui/Dialog';
import { PickupForm } from '@/components/pickups/PickupForm';
import { cn } from '@/lib/cn';

const STATUS_STYLE: Record<string, string> = {
  scheduled: 'bg-fill text-ink-dim',
  in_progress: 'bg-status-onsite text-status-onsite-fg',
  complete: 'bg-status-complete text-status-complete-fg',
  cancelled: 'bg-fill text-ink-faint line-through',
};
const STATUS_LABEL: Record<string, string> = {
  scheduled: 'Scheduled',
  in_progress: 'In progress',
  complete: 'Complete',
  cancelled: 'Cancelled',
};

function fmtWindow(start: string | null, end: string | null): string {
  if (!start) return 'Unscheduled';
  const s = new Date(start);
  const d = s.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
  const t = (iso: string) =>
    new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return end ? `${d} · ${t(start)}–${t(end)}` : `${d} · ${t(start)}`;
}

export function Pickups() {
  const { data, isLoading, error } = usePickups();
  const [creating, setCreating] = useState(false);
  const pickups = data?.data ?? [];

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-4">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <PackageCheck className="w-6 h-6 text-ink-faint" /> Pickups
          </h1>
          <p className="text-sm text-ink-dim">
            Warehouse runs — one trip, the orders it covers, and who’s on it.
          </p>
        </div>
        <Button onClick={() => setCreating(true)}>
          <Plus className="w-4 h-4" /> New pickup
        </Button>
      </header>

      {isLoading ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-ink-dim">Loading…</CardContent>
        </Card>
      ) : error ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-status-blocked-fg">
            {(error as Error).message}
          </CardContent>
        </Card>
      ) : pickups.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-ink-dim">
            No pickups yet. Create one to plan a warehouse run.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <ul className="divide-y divide-line">
            {pickups.map((p) => (
              <li key={p.id}>
                <Link
                  to={`/pickups/${p.id}`}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-fill-soft"
                >
                  <Truck className="w-4 h-4 text-ink-faint shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate flex items-center gap-2">
                      {p.display_title}
                      {p.all_picked_up && p.project_count > 0 && (
                        <CheckCircle2 className="w-3.5 h-3.5 text-status-complete-fg" />
                      )}
                    </div>
                    <div className="text-[11px] text-ink-faint">
                      {fmtWindow(p.scheduled_start, p.scheduled_end)} · {p.project_count}{' '}
                      {p.project_count === 1 ? 'order' : 'orders'} · {p.crew_count}{' '}
                      {p.crew_count === 1 ? 'crew' : 'crew'}
                    </div>
                  </div>
                  <span
                    className={cn(
                      'shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium',
                      STATUS_STYLE[p.status] ?? 'bg-fill text-ink-dim',
                    )}
                  >
                    {STATUS_LABEL[p.status] ?? p.status}
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {creating && <NewPickupDialog onClose={() => setCreating(false)} />}
    </div>
  );
}

function NewPickupDialog({ onClose }: { onClose: () => void }) {
  const create = useCreatePickup();
  return (
    <Dialog open onClose={onClose} busy={create.isPending} title={<>New pickup</>} className="max-w-lg">
      <PickupForm
        submitLabel="Create pickup"
        pending={create.isPending}
        onCancel={onClose}
        onSubmit={(v) =>
          create.mutate(
            { ...v, project_ids: v.project_ids },
            {
              onSuccess: () => {
                toast.success('Pickup created');
                onClose();
              },
              onError: (e) => toast.error(`Could not create: ${e.message}`),
            },
          )
        }
      />
    </Dialog>
  );
}
